import { z } from "zod";
import { Cent, CentPositiv, Email, Prozent, Slug } from "./basis";
import { Plan, PLAN_STANDARD } from "../plan";

export const Oeffnungszeit = z.object({
  von: z.string().regex(/^\d{2}:\d{2}$/),
  bis: z.string().regex(/^\d{2}:\d{2}$/),
});

/** Versioniertes Einstellungs-JSON je Laden (`laeden.einstellungen_json`). Fehlende Werte → Standard. */
export const LadenEinstellungen = z.object({
  version: z.literal(1).default(1),
  oeffnungszeiten: z
    .record(z.enum(["mo", "di", "mi", "do", "fr", "sa", "so"]), Oeffnungszeit.nullable())
    .default({ mo: null, di: { von: "10:00", bis: "18:00" }, mi: { von: "10:00", bis: "18:00" }, do: { von: "10:00", bis: "18:00" }, fr: { von: "10:00", bis: "18:00" }, sa: { von: "10:00", bis: "14:00" }, so: null }),
  abgleich: z
    .object({
      vorlaufSekunden: z.number().int().min(0).max(300).default(15),
      nachlaufMinuten: z.number().int().min(1).max(120).default(15),
    })
    .default({ vorlaufSekunden: 15, nachlaufMinuten: 15 }),
  kasse: z
    .object({
      sperreNachMinuten: z.number().int().min(1).max(60).default(5),
      rabattFreigabeAbProzent: Prozent.default(20),
      preisChipsCent: z.array(CentPositiv).length(8).default([100, 200, 300, 500, 800, 1000, 1500, 2000]),
      scanTon: z.boolean().default(true),
    })
    .default({ sperreNachMinuten: 5, rabattFreigabeAbProzent: 20, preisChipsCent: [100, 200, 300, 500, 800, 1000, 1500, 2000], scanTon: true }),
  preise: z
    .object({
      modell: z.enum(["miete", "provision", "beides"]).default("beides"),
      provisionProzent: Prozent.default(20),
      kautionCent: CentPositiv.default(0),
      kautionVerrechnen: z.boolean().default(true),
      /** Miet-Abo anbieten (Etappe 3, Antwort „beides wählbar“). */
      aboErlaubt: z.boolean().default(true),
      kuendigungsfristWochen: z.union([z.literal(2), z.literal(4)]).default(2),
    })
    .default({ modell: "beides", provisionProzent: 20, kautionCent: 0, kautionVerrechnen: true, aboErlaubt: true, kuendigungsfristWochen: 2 }),
  abrechnung: z
    .object({
      periode: z.enum(["woche", "monat"]).default("monat"),
      mindestbetragCent: CentPositiv.default(1000),
      auszahlungswege: z.array(z.enum(["ueberweisung", "bar"])).default(["ueberweisung", "bar"]),
    })
    .default({ periode: "monat", mindestbetragCent: 1000, auszahlungswege: ["ueberweisung", "bar"] }),
  regeln: z
    .object({
      preisMinCent: CentPositiv.default(50),
      preisMaxCent: CentPositiv.default(50_000),
      vertragsvorlage: z.string().default(""),
      /** E10.6: Wörter, bei denen das Erfassen warnt (z. B. Waffen, Lebensmittel, Elektro ohne CE). */
      verboteneWaren: z.array(z.string().trim().min(2).max(40)).max(50).default([]),
      /** E10.6: Preisgrenzen je Kategorie (überschreiben die allgemeinen Grenzen). */
      kategorieGrenzen: z.array(z.object({ kategorie: z.string().trim().min(1).max(40), minCent: CentPositiv, maxCent: CentPositiv })).max(30).default([]),
    })
    .default({ preisMinCent: 50, preisMaxCent: 50_000, vertragsvorlage: "", verboteneWaren: [], kategorieGrenzen: [] }),
  /** E10.4: Schließtage (eigene Tage) und gesetzliche Feiertage Schleswig-Holstein. */
  schliesstage: z.object({ tage: z.array(z.string().regex(/^\d{4}-\d{2}-\d{2}$/)).max(60).default([]), feiertageSH: z.boolean().default(true) }).default({ tage: [], feiertageSH: true }),
  branding: z
    .object({
      akzent: z.string().regex(/^#[0-9a-fA-F]{6}$/).nullable().default(null),
      logoKey: z.string().nullable().default(null),
    })
    .default({ akzent: null, logoKey: null }),
  /** E9: SEPA-Lastschrift (Gläubiger-ID des Ladens) und Mahnwesen. */
  /** E14.3 Kommission: Ware ohne Regal annehmen, eigene Provision. */
  kommission: z.object({ aktiv: z.boolean().default(false), provisionProzent: Prozent.default(40) }).default({ aktiv: false, provisionProzent: 40 }),
  /** E14.5 Kundenbindung: öffentliche „Neu im Laden“-Seite und Newsletter-Anmeldung. */
  kunden: z.object({ neuImLaden: z.boolean().default(true), newsletter: z.boolean().default(false) }).default({ neuImLaden: true, newsletter: false }),
  sepa: z.object({ glaeubigerId: z.string().trim().max(35).default(""), vorlaufTage: z.number().int().min(2).max(14).default(6) }).default({ glaeubigerId: "", vorlaufTage: 6 }),
  mahnwesen: z
    .object({
      aktiv: z.boolean().default(false),
      erinnerungTage: z.number().int().min(1).max(60).default(7),
      mahnung1Tage: z.number().int().min(2).max(90).default(14),
      mahnung2Tage: z.number().int().min(3).max(120).default(28),
      gebuehr1Cent: CentPositiv.default(0),
      gebuehr2Cent: CentPositiv.default(500),
      sperreNachStufe3: z.boolean().default(true),
    })
    .default({ aktiv: false, erinnerungTage: 7, mahnung1Tage: 14, mahnung2Tage: 28, gebuehr1Cent: 0, gebuehr2Cent: 500, sperreNachStufe3: true }),
  /** Mieterportal (MP-D): Dürfen Mieter eigene Aktionen anlegen, und wie weit runter? */
  portal: z
    .object({
      aktionenErlaubt: z.boolean().default(true),
      aktionMaxProzent: z.number().int().min(5).max(90).default(50),
      /** E12.2: Gutschrift für die werbende Mieterin, sobald die Geworbene ihren ersten Vertrag bestätigt (0 = keine Prämie). */
      werbepraemieCent: CentPositiv.default(0),
    })
    .default({ aktionenErlaubt: true, aktionMaxProzent: 50, werbepraemieCent: 0 }),
  /** Ladenplan: Raster, feste Elemente, Grundriss-Bild (Regalplätze stehen am Regal). */
  plan: Plan.default(PLAN_STANDARD),
  /** Einrichtungsassistent: erledigte Schritte und Abschluss. */
  einrichtung: z
    .object({
      erledigt: z.array(z.enum(["laden", "preise", "regale", "sumup", "personal"])).default([]),
      abgeschlossenAt: z.string().nullable().default(null),
    })
    .default({ erledigt: [], abgeschlossenAt: null }),
});
export type LadenEinstellungen = z.infer<typeof LadenEinstellungen>;

export const UstModus = z.enum(["regel19", "kleinunternehmer"]);

export const Adresse = z.object({
  strasse: z.string().trim().max(120).default(""),
  plz: z.string().trim().max(10).default(""),
  ort: z.string().trim().max(80).default(""),
});

export const LadenAnlegen = z.object({
  name: z.string().trim().min(2).max(80),
  slug: Slug,
  inhaberEmail: Email,
  inhaberName: z.string().trim().min(1).max(80),
});
export type LadenAnlegen = z.infer<typeof LadenAnlegen>;

export const LadenStammdaten = z.object({
  name: z.string().trim().min(2).max(80),
  adresse: Adresse,
  telefon: z.string().trim().max(40).default(""),
  email: Email.or(z.literal("")).default(""),
  steuernummer: z.string().trim().max(40).default(""),
  ustModus: UstModus.default("regel19"),
});
export type LadenStammdaten = z.infer<typeof LadenStammdaten>;

export const Betrag = Cent;
